/** Оболочка: бордовая боковая панель (десктоп) и нижняя панель из 5 иконок (телефон). */
import { CalendarDays, FolderOpen, LogOut, PenLine, Settings, Sparkles, TriangleAlert, type LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import type { AutoStatus, PostFull, Stats } from "@impuls/shared";
import { api } from "../lib/api";
import { useLogout, useMe } from "../lib/auth";
import { EcgMark } from "./Ecg";
import { useGlobalStream } from "../lib/streams";

interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: LucideIcon;
  badge?: (s: Stats) => number;
  /** пульсирующая точка, пока Claude готовит автопосты */
  live?: boolean;
}

export const NAV: NavItem[] = [
  { to: "/write", label: "Написать пост", short: "Написать", icon: PenLine, badge: (s) => s.drafts_author },
  { to: "/auto", label: "Автоконтент", short: "Авто", icon: Sparkles, badge: (s) => s.drafts_auto, live: true },
  { to: "/calendar", label: "Календарь", short: "Календарь", icon: CalendarDays },
  { to: "/projects", label: "Проекты", short: "Проекты", icon: FolderOpen },
  { to: "/settings", label: "Настройки", short: "Настройки", icon: Settings },
];

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<Stats>("/api/stats"),
    refetchInterval: 30_000,
    retry: false,
  });
}

/** vk_group_token_set добавляет сервер; старый сервер его не присылает. */
type StatsVk = Stats & { vk_group_token_set?: boolean };

/** Вход VK ID истёк. С ключом сообщества посты всё равно уходят (только текст) — мягкое предупреждение. */
export function ReloginBanner({ stats }: { stats: StatsVk | undefined }) {
  if (!stats?.vk_needs_relogin) return null;
  const keyOn = !!stats.vk_group_token_set;
  return (
    <div className={`relogin ${keyOn ? "relogin--warn" : ""}`} role={keyOn ? "status" : "alert"}>
      <TriangleAlert size={18} aria-hidden="true" />
      <span>
        {keyOn
          ? "Вход VK ID истёк — публикуем ключом сообщества (только текст)."
          : "Вход во ВКонтакте истёк — отложенные посты не будут создаваться."}
      </span>
      <Link to="/settings#vk" className="relogin__link">
        Войти заново
      </Link>
    </div>
  );
}

/** Идёт ли сейчас подборка или «Нужен пост» — тот же запрос, что на странице Автоконтента (обновляет общий поток). */
function useAutoRunning() {
  const { data } = useQuery({
    queryKey: ["auto-status"],
    queryFn: () => api.get<AutoStatus>("/api/auto/status"),
    refetchInterval: 30_000,
    retry: false,
  });
  return (data?.running.length ?? 0) > 0;
}

export function Shell() {
  const { data: me } = useMe();
  const { data: stats } = useStats();
  const autoRunning = useAutoRunning();
  const logout = useLogout();
  const loc = useLocation();
  useGlobalStream();
  // в редакторе — раздел по типу поста: автопост открыт из Автоконтента (пост уже в кэше редактора)
  const postId = Number(loc.pathname.match(/^\/posts\/(\d+)/)?.[1] ?? 0);
  const { data: openPost } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => api.get<PostFull>(`/api/posts/${postId}`),
    enabled: false,
  });
  const section = postId ? NAV[openPost && openPost.source !== "author" ? 1 : 0] : NAV.find((n) => loc.pathname.startsWith(n.to));

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        К содержимому
      </a>
      <aside className="sidebar" aria-label="Главное меню">
        <Link to="/write" className="sidebar__brand" aria-label="Импульс · Контент-студия — на главную">
          <img src="/brand/logo-white.png" alt="Импульс" width={176} height={70} />
          <span className="sidebar__product">Контент-студия</span>
        </Link>
        <nav className="sidebar__nav">
          {NAV.map((n) => {
            const count = stats && n.badge ? n.badge(stats) : 0;
            const live = !!n.live && autoRunning;
            return (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-item ${isActive ? "is-active" : ""}`}>
                {({ isActive }) => (
                  <>
                    <n.icon size={20} strokeWidth={1.9} aria-hidden="true" />
                    <span className="nav-item__label">
                      {n.label}
                      {isActive && <EcgMark className="nav-item__pulse ecg-mark--draw" />}
                    </span>
                    {count > 0 && (
                      <span className="nav-item__badge" aria-label={`${count} ждут`}>
                        {count}
                      </span>
                    )}
                    {live && <span className="nav-item__live" title="Claude готовит автопосты" aria-label="Claude готовит автопосты" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar__foot">
          {me?.user && (
            <div className="sidebar__user">
              <span className="sidebar__avatar" aria-hidden="true">
                {me.user.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="sidebar__name">{me.user.name}</span>
            </div>
          )}
          {me?.localBypass ? (
            <span className="sidebar__hint">Вход без пароля с этого ПК</span>
          ) : (
            <button className="sidebar__logout" onClick={logout}>
              <LogOut size={16} aria-hidden="true" />
              Выйти
            </button>
          )}
        </div>
      </aside>

      <header className="topbar">
        <Link to="/write" className="topbar__brand" aria-label="На главную">
          <img src="/brand/heart-white.png" alt="" width={34} height={28} />
        </Link>
        <span className="topbar__title">{section?.label ?? "Контент-студия"}</span>
        {!me?.localBypass && (
          <button className="topbar__logout" onClick={logout} aria-label="Выйти">
            <LogOut size={18} />
          </button>
        )}
      </header>

      <main id="main" className="main">
        <ReloginBanner stats={stats} />
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Разделы">
        {NAV.map((n) => {
          const count = stats && n.badge ? n.badge(stats) : 0;
          const live = !!n.live && autoRunning;
          return (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `tab ${isActive ? "is-active" : ""}`}>
              <span className="tab__icon">
                <n.icon size={22} strokeWidth={1.9} aria-hidden="true" />
                {count > 0 && <span className="tab__badge">{count > 9 ? "9+" : count}</span>}
                {live && <span className="tab__live" />}
              </span>
              <span className="tab__label">{n.short}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
